import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import React from "react";
import {
  TableContainer,
  Paper,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  IconButton,
  TableSortLabel,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import Flag from "react-flagkit";
import {
  Product,
  GiftCardTypeExtendDate,
  GiftCardListQuery,
} from "../../../utils";
import { deleteGiftCard } from "../../../utils";
import { Archive } from "@mui/icons-material";
import ManualAssignGiftcardDialog from "features/admin/giftcards/ManualAssignGiftcardDialog";

type SortableKey = NonNullable<GiftCardListQuery["sortBy"]>;

interface AllGiftCardsTableProps {
  giftCards: GiftCardTypeExtendDate[];
  totalCount: number;
  productMap: Record<string, Product>;

  allCategories: string[];
  allProviders: string[];
  allCountries: string[];
  onWriteOff: (giftCard: GiftCardTypeExtendDate) => void;
  refreshGiftCards: () => void;
  setCurrentGiftCard: (giftCard: GiftCardTypeExtendDate | null) => void;
  setOpenDialog: (open: boolean) => void;

  page: number;
  limit: number;
  order: "asc" | "desc";
  orderBy: SortableKey;
  filters: { category: string; provider: string; country: string };
  search: string;

  onChangeQuery: (partial: {
    search?: string;
    category?: string;
    provider?: string;
    country?: string;
  }) => void;
  onChangePage: (page: number) => void;
  onChangeLimit: (limit: number) => void;
  onChangeSort: (sortBy: SortableKey, order: "asc" | "desc") => void;
}

const AllGiftCardsTable: React.FC<AllGiftCardsTableProps> = ({
  giftCards,
  totalCount,
  productMap,
  allCategories,
  allProviders,
  allCountries,
  refreshGiftCards,
  setCurrentGiftCard,
  setOpenDialog,
  page,
  limit,
  order,
  orderBy,
  filters,
  search,
  onChangeQuery,
  onChangePage,
  onChangeLimit,
  onChangeSort,
  onWriteOff,
}) => {
  const getProduct = (productId: string) => productMap[productId];
  const fmt = (n: number | string | undefined) =>
    Number.isFinite(Number(n)) ? Number(n).toFixed(2) : "0.00";

  const [assignOpen, setAssignOpen] = React.useState(false);
  const [assignGiftCardId, setAssignGiftCardId] = React.useState<string | null>(
    null
  );

  const openAssign = (giftCardId: string) => {
    setAssignGiftCardId(giftCardId);
    setAssignOpen(true);
  };

  const closeAssign = () => {
    setAssignOpen(false);
    setAssignGiftCardId(null);
  };

  const onAssignSuccess = () => {
    refreshGiftCards();
  };

  const sortableColumns: ReadonlyArray<{
    id: SortableKey;
    label: string;
    type: "text" | "num" | "date" | "bool";
  }> = [
    { id: "fullname", label: "Fullname", type: "text" },
    { id: "productId", label: "Product ID", type: "text" },
    { id: "status", label: "Status", type: "text" },
    { id: "isActive", label: "Active", type: "bool" },
    { id: "cost", label: "Cost", type: "num" },
    { id: "addedTime", label: "Added", type: "date" },
    { id: "lastModified", label: "Last Modified", type: "date" },
  ];

  const displayOnly: ReadonlyArray<{ id: string; label: string }> = [
    { id: "productLabel", label: "Product" },
    { id: "priceUSD", label: "Current Price USD" },
    { id: "code", label: "Code" },
    { id: "category", label: "Category" },
    { id: "provider", label: "Provider" },
    { id: "country", label: "Country" },
  ];

  const handleSortClick = (id: SortableKey) => {
    const isAsc = orderBy === id && order === "asc";
    onChangeSort(id, isAsc ? "desc" : "asc");
  };

  const handleEdit = (gc: GiftCardTypeExtendDate) => {
    setCurrentGiftCard(gc);
    setOpenDialog(true);
  };

  const handleDelete = async (id: string) => {
    await deleteGiftCard(id);
    refreshGiftCards();
  };

  return (
    <TableContainer component={Paper} sx={{ marginTop: 4 }}>
      <Typography variant="h6">All Gift Cards</Typography>

      <TextField
        variant="outlined"
        placeholder="Search by Product/Name/Code/ID..."
        value={search}
        onChange={(e) => onChangeQuery({ search: e.target.value })}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        sx={{ marginBottom: 2, width: "30%" }}
      />

      <div style={{ display: "flex", gap: 16, marginBottom: 8 }}>
        <FormControl sx={{ minWidth: 160 }}>
          <InputLabel>Category</InputLabel>
          <Select
            value={filters.category}
            label="Category"
            onChange={(e) =>
              onChangeQuery({ category: String(e.target.value) })
            }
            MenuProps={{ PaperProps: { style: { maxHeight: 240 } } }}
          >
            <MenuItem value="">All</MenuItem>
            {allCategories.map((c) => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 160 }}>
          <InputLabel>Provider</InputLabel>
          <Select
            value={filters.provider}
            label="Provider"
            onChange={(e) =>
              onChangeQuery({ provider: String(e.target.value) })
            }
            MenuProps={{ PaperProps: { style: { maxHeight: 240 } } }}
          >
            <MenuItem value="">All</MenuItem>
            {allProviders.map((p) => (
              <MenuItem key={p} value={p}>
                {p}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 160 }}>
          <InputLabel>Country</InputLabel>
          <Select
            value={filters.country}
            label="Country"
            onChange={(e) => onChangeQuery({ country: String(e.target.value) })}
            MenuProps={{ PaperProps: { style: { maxHeight: 240 } } }}
          >
            <MenuItem value="">All</MenuItem>
            {allCountries.map((c) => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      <Table>
        <TableHead>
          <TableRow>
            {/* Sortable columns */}
            {sortableColumns.map((col) => (
              <TableCell
                key={col.id}
                sortDirection={orderBy === col.id ? order : false}
                sx={{ minWidth: 140 }}
              >
                <TableSortLabel
                  active={orderBy === col.id}
                  direction={orderBy === col.id ? order : "asc"}
                  onClick={() => handleSortClick(col.id)}
                >
                  {col.label}
                </TableSortLabel>
              </TableCell>
            ))}
            {/* Non-sort display columns */}
            {displayOnly.map((c) => (
              <TableCell key={c.id} sx={{ minWidth: 160 }}>
                {c.label}
              </TableCell>
            ))}
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {giftCards.map((gc) => {
            const prod = getProduct(gc.productId);
            const isAvailable = gc.status === "available";
            return (
              <TableRow key={gc.id}>
                {/* sortable */}
                <TableCell>{gc.fullname}</TableCell>
                <TableCell>{gc.productId}</TableCell>
                <TableCell sx={{ textTransform: "capitalize" }}>
                  {gc.status}
                </TableCell>
                <TableCell>{gc.isActive ? "Yes" : "No"}</TableCell>
                <TableCell>{fmt(gc.cost)}</TableCell>

                <TableCell>
                  {gc.addedTime ? new Date(gc.addedTime).toLocaleString() : "—"}
                </TableCell>
                <TableCell>
                  {gc.lastModified
                    ? new Date(gc.lastModified).toLocaleString()
                    : "—"}
                </TableCell>

                {/* display-only */}
                <TableCell>
                  {prod
                    ? `${prod.name} ${prod.price} ${prod.currency}`
                    : "Unknown Product"}
                </TableCell>
                <TableCell>{prod ? fmt(prod.priceUSD) : "0.00"}</TableCell>
                <TableCell>{gc.code}</TableCell>
                <TableCell>{prod?.category ?? ""}</TableCell>
                <TableCell>{prod?.provider ?? ""}</TableCell>
                <TableCell>
                  {prod?.countryCode ? <Flag country={prod.countryCode} /> : ""}
                </TableCell>
                <TableCell>
                  {isAvailable && (
                    <IconButton
                      aria-label="assign"
                      color="primary"
                      onClick={() => openAssign(gc.id)}
                      title="Attribuer manuellement"
                    >
                      <PersonAddAlt1Icon />
                    </IconButton>
                  )}

                  <IconButton onClick={() => handleEdit(gc)} aria-label="edit">
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => onWriteOff(gc)}
                    aria-label="write-off"
                    color="warning"
                  >
                    <Archive />
                  </IconButton>

                  <IconButton
                    onClick={() => handleDelete(gc.id)}
                    aria-label="delete"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <TablePagination
        rowsPerPageOptions={[25, 40, 50, 100]}
        component="div"
        count={totalCount}
        rowsPerPage={limit}
        page={Math.max(0, (page ?? 1) - 1)}
        onPageChange={(_, newPage) => onChangePage(newPage + 1)}
        onRowsPerPageChange={(e) => onChangeLimit(parseInt(e.target.value, 10))}
      />

      <ManualAssignGiftcardDialog
        open={assignOpen}
        onClose={closeAssign}
        defaultGiftCardId={assignGiftCardId ?? undefined}
        onSuccess={onAssignSuccess}
      />
    </TableContainer>
  );
};

export default AllGiftCardsTable;
